import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  BuildingOffice2Icon,
  StarIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ClockIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  CheckCircleIcon as CheckCircleOutline,
  SparklesIcon,
  BoltIcon,
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  UserGroupIcon,
  BanknotesIcon,
  KeyIcon,
  Square2StackIcon,
  IdentificationIcon,
  UserIcon,
  GlobeAltIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import {
  CurrencyRupeeIcon,
} from '@heroicons/react/24/solid';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../SEO/SEO';

const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }
  return html;
};

const DEFAULT_CALCULATOR_NOTICE_TEXT = '<p>Not sure how much home loan you may qualify for? Use our <a href="https://ahamhfc.com/customer-360-eligibility-calculator"><strong><u>home loan eligibility calculator</u></strong></a> to get an initial estimate before you apply.</p>';

const HomeConstructionLoan = () => {
  const navigate = useNavigate();
  const LOAN_TYPE = 'home_construction_loan';
  const { i18n } = useTranslation();

  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('https://ahamhfc.com/') || href.startsWith('http://ahamhfc.com/')) {
      const path = href.replace(/^https?:\/\/ahamhfc\.com/, '');
      if (path.startsWith('/')) {
        e.preventDefault();
        navigate(path);
      }
    }
  };

  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");

  // Banner state
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Fallback static banner data
  const fallbackBanner = {
    title: 'Build Your Dream Home with Aham Home Construction Loan',
    subtitle: 'No salary slips or ITR? No problem. Aham helps you build your home with easy construction financing.',
    image_url: 'img/construction-loan/Home-Construction-Banner.webp',
    primary_button_text: 'Apply Now',
    primary_button_link: 'https://aham.mbwhost.in/contact',
    highlights: []
  };

  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Key Benefits state
  const [dynamicBenefits, setDynamicBenefits] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState({
    section_title: 'Key Benefits & Features',
    section_subtitle: 'Discover why Aham Home Construction Loan is the right choice for building your dream home'
  });
  const [benefitsLoading, setBenefitsLoading] = useState(true);

  // Eligibility state
  const [dynamicEligibility, setDynamicEligibility] = useState([]);
  const [eligibilitySettings, setEligibilitySettings] = useState({
    section_title: 'Home Construction Loan Eligibility — Who Can Apply?',
    section_subtitle: "You do not need to be a salaried employee with a perfect credit score to apply for a home construction loan with Aham. We assess your real repayment capacity — through a personal interview, field visit, and cash-flow evaluation.",
    calculator_notice_text: DEFAULT_CALCULATOR_NOTICE_TEXT
  });
  const [eligibilityLoading, setEligibilityLoading] = useState(true);

  // Documents state
  const [dynamicDocuments, setDynamicDocuments] = useState({});
  const [documentsSettings, setDocumentsSettings] = useState({
    section_title: 'Documents Required',
    section_subtitle: 'Keep these documents ready for a smooth application process'
  });
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Interest Rates state
  const [dynamicInterestCards, setDynamicInterestCards] = useState([]);
  const [interestSettings, setInterestSettings] = useState({
    section_title: 'Affordable Home Loan',
    section_subtitle: 'Interest Rates',
    intro_text: 'Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make home construction affordable and stress-free.',
    closing_text: 'Begin your home construction journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs.',
    cta_text: 'Calculate EMI',
    cta_link: 'https://aham.mbwhost.in/emi-calculator'
  });
  const [interestLoading, setInterestLoading] = useState(true);

  // FAQ state
  const [dynamicFAQs, setDynamicFAQs] = useState([]);
  const [faqSettings, setFaqSettings] = useState({
    section_title: 'Frequently Asked Questions',
    section_subtitle: 'Find answers to common questions about Home Construction Loan'
  });
  const [faqLoading, setFaqLoading] = useState(true);

  // CTA Banner state
  const [ctaBannerSettings, setCtaBannerSettings] = useState({
    title: 'Ready to Start Building Your Dream Home?',
    description: 'Take the first step towards constructing your perfect home with Aham\'s hassle-free loan process',
    button_text: 'Talk to Our Loan Expert',
    button_link: 'https://aham.mbwhost.in/contact'
  });
  const [ctaBannerLoading, setCtaBannerLoading] = useState(true);

  // Icon mapping
  const iconMap = {
    HomeIcon,
    BuildingOffice2Icon,
    StarIcon,
    ShieldCheckIcon,
    CurrencyDollarIcon,
    ClockIcon,
    DocumentCheckIcon,
    CheckCircleIcon: CheckCircleOutline,
    SparklesIcon,
    BoltIcon,
    HeartIcon,
    LightBulbIcon,
    RocketLaunchIcon,
    ChartBarIcon,
    UserGroupIcon,
    BanknotesIcon,
    KeyIcon,
    CurrencyRupeeIcon,
    UserIcon,
    GlobeAltIcon,
    BriefcaseIcon,
    DocumentTextIcon,
    Square2StackIcon,
    IdentificationIcon
  };

  // Fallback static overview data
  const fallbackOverview = {
    title: 'Home Construction Loan',
    description: `Building your dream home is a significant milestone, and Aham Housing Finance is here to make it easier. Our Home Construction Loan is designed to provide financial assistance for constructing a new house on land you already own.

Whether you're planning a simple structure or a luxury villa, we offer flexible loan options tailored to your construction needs.`,
    features: [
      'No strict income proof requirements – We understand your unique financial situation',
      'Flexible disbursement – Funds released in stages as construction progresses',
      'Competitive interest rates – Affordable rates designed to suit your budget',
      'Quick approval process – Fast-track processing to get you started sooner'
    ],
    image_url: '/img/construction-loan/2.webp'
  };


  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Key Benefits Data
  const benefits = [
    {
      icon: BanknotesIcon,
      title: 'Loan up to ₹30 Lakhs',
      description: 'Get substantial funding for your dream home construction'
    },
    {
      icon: ClockIcon,
      title: 'Tenure up to 20 Years',
      description: 'Flexible repayment options that suit your financial planning'
    },
    {
      icon: ChartBarIcon,
      title: 'Disbursement Linked to Progress',
      description: 'Funds released based on construction milestones'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Competitive Interest Rates',
      description: 'Enjoy attractive rates designed for your benefit'
    },
    {
      icon: CurrencyRupeeIcon,
      title: 'No Prepayment Penalty',
      description: 'Pay off your loan early without any additional charges'
    },
    {
      icon: HomeIcon,
      title: 'Covers Renovation & Repair',
      description: 'Finance both new construction and home improvements'
    }
  ];

  // Eligibility Criteria Static Fallback
  const eliitems = [
    { text: 'Age: 21 to 65 years', icon: User, from: "from-blue-500", to: "to-cyan-400" },
    { text: 'Indian Residents or NRIs', icon: Globe, from: "from-green-500", to: "to-lime-400" },
    { text: 'Salaried, Self-Employed, or Business Professionals', icon: Briefcase, from: "from-pink-500", to: "to-rose-400" },
    { text: 'Stable income source', icon: DollarSign, from: "from-orange-500", to: "to-amber-400" },
    { text: 'Approved construction plan and building permit', icon: FileText, from: "from-purple-500", to: "to-indigo-400" },
    { text: 'Clear land title', icon: Shield, from: "from-teal-500", to: "to-emerald-400" },
    { text: 'Good credit history preferred but not mandatory', icon: Star, from: "from-yellow-500", to: "to-orange-400" },
    { text: 'Minimum plot size requirement', icon: Ruler, from: "from-red-500", to: "to-orange-400" }
  ];

  // Documents Required
  const documents = {
    common: [
      'Identity Proof (Aadhaar, PAN, Passport, Voter ID)',
      'Address Proof (Utility Bills, Aadhaar)',
      'Recent Passport-size Photographs',
      'Bank Statements (Last 6 months)'
    ],
    salaried: [
      'Last 3 Months Salary Slips',
      'Form 16 / IT Returns (Last 2 Years)',
      'Employment Letter / Appointment Letter',
      'Employee ID Card'
    ],
    selfEmployed: [
      'Business Registration Documents',
      'IT Returns / Audited Financials (Last 2-3 Years)',
      'GST Returns (if applicable)',
      'Business Address Proof',
      'Bank Statements of Business Account',
      "Business License / Trade License"
    ],
    nri: [
      'Valid Passport & Visa',
      'Overseas Address Proof',
      'NRE/NRO Bank Statements',
      'Employment Contract / Proof of Income Abroad',
      'Power of Attorney (if applicable)',
      "OCI/PIO Card (if applicable)"
    ],
    property: [
      'Land Purchase Agreement / Sale Deed',
      'Title Deed & Encumbrance Certificate',
      'Approved Building Plan from Local Authority',
      'NOC from Society / Local Authority',
      'Construction Estimate / Cost Sheet',
      'Property Tax Receipts',
      'Land Survey Documents',
      "Soil Test Report (if applicable)"
    ]
  };

  // FAQs
  const faqs = [
    {
      question: 'What is a Home Construction Loan?',
      answer: 'A Home Construction Loan helps you build a new house on a plot of land you already own. Aham Housing Finance provides funds in stages as construction progresses, ensuring financial support throughout your building journey.'
    },
    {
      question: 'Do I need income proof to apply?',
      answer: 'At Aham, we understand that not everyone has traditional income documents. While having ITR or salary slips helps expedite approval, we also consider alternative income verification methods to support your loan application.'
    },
    {
      question: 'How is the loan amount disbursed?',
      answer: 'The loan is disbursed in stages based on construction progress. Typically: 20% on completion of foundation, 35% on completion of lintel level, 25% on completion of roof, and 20% on final completion. Our team conducts site inspections before each disbursement.'
    },
    {
      question: 'What is the maximum loan tenure?',
      answer: 'Aham offers flexible repayment tenures of up to 20 years, allowing you to choose a repayment schedule that aligns with your financial capabilities and future plans.'
    },
    {
      question: 'Can I prepay my loan without penalty?',
      answer: 'Yes! Aham Housing Finance does not charge any prepayment penalty. You can pay off your loan partially or fully at any time without incurring additional costs, helping you save on interest.'
    },
    {
      question: 'What if I already own land?',
      answer: 'Perfect! Our Home Construction Loan is designed for individuals who own a plot or land and wish to construct a residential property on it. Just ensure you have clear land title and approved building plans.'
    },
    {
      question: 'Are NRIs eligible for this loan?',
      answer: 'Yes, NRIs are eligible to apply for Home Construction Loans from Aham. You will need to provide documents such as passport, visa, overseas address proof, and NRE/NRO bank statements along with standard property documents.'
    },
    {
      question: 'How long does the approval process take?',
      answer: 'Aham strives for quick and efficient processing. Once all documents are submitted, loan approval typically takes 7-10 working days. Our dedicated team works to minimize delays and keep you informed throughout the process.'
    },
    {
      question: 'Can I use the loan for renovation or extension?',
      answer: 'Yes! In addition to new construction, Aham Home Construction Loans can also be used for significant home renovations, extensions, or repairs to your existing property, giving you flexibility in how you use the funds.'
    }
  ];

  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  // Fetch banner data on component mount
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/admin/banners/page/home_construction_loan');
        const banners = response.data.data || [];

        if (banners.length > 0) {
          // Use first active banner for this page
          setBanner(banners[0]);
        } else {
          // Use fallback if no banner found
          setBanner(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        // Use fallback on error
        setBanner(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBanner();
  }, []);

  // Fetch overview data on component mount
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        const response = await axios.get('/api/admin/loan-overview/home_construction_loan');
        const data = response.data.data || null;

        if (data) {
          // Parse features if it's a string
          const parsedData = {
            ...data,
            features: typeof data.features === 'string' ? JSON.parse(data.features) : data.features
          };
          setOverview(parsedData);
        } else {
          // Use fallback if no overview found
          setOverview(fallbackOverview);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
        // Use fallback on error
        setOverview(fallbackOverview);
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
  }, []);

  // Fetch dynamic benefits and settings
  useEffect(() => {
    const fetchBenefitsData = async () => {
      try {
        setBenefitsLoading(true);
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/key-benefits/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/key-benefits/settings/${LOAN_TYPE}`)
        ]);

        if (benefitsRes.data.success) {
          setDynamicBenefits(benefitsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setBenefitsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching benefits data:', error);
      } finally {
        setBenefitsLoading(false);
      }
    };

    fetchBenefitsData();
  }, []);

  // Fetch dynamic eligibility and settings
  useEffect(() => {
    const fetchEligibilityData = async () => {
      try {
        setEligibilityLoading(true);
        const [criteriaRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/eligibility/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/eligibility/settings/${LOAN_TYPE}`)
        ]);

        if (criteriaRes.data.success) {
          setDynamicEligibility(criteriaRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setEligibilitySettings(prev => ({
            ...prev,
            ...settingsRes.data.data,
            calculator_notice_text: settingsRes.data.data.calculator_notice_text || DEFAULT_CALCULATOR_NOTICE_TEXT
          }));
        }
      } catch (error) {
        console.error('Error fetching eligibility data:', error);
      } finally {
        setEligibilityLoading(false);
      }
    };

    fetchEligibilityData();
  }, []);

  // Fetch dynamic documents and settings
  useEffect(() => {
    const fetchDocumentsData = async () => {
      try {
        setDocumentsLoading(true);
        const [documentsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/documents/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/documents/settings/${LOAN_TYPE}`)
        ]);

        if (documentsRes.data.success) {
          // Group documents by category
          const grouped = {};
          documentsRes.data.data.forEach(doc => {
            if (!grouped[doc.category]) {
              grouped[doc.category] = [];
            }
            grouped[doc.category].push(doc.document_name);
          });
          setDynamicDocuments(grouped);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setDocumentsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching documents data:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocumentsData();
  }, []);

  // Fetch dynamic interest rates and settings
  useEffect(() => {
    const fetchInterestData = async () => {
      try {
        setInterestLoading(true);
        const [cardsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/interest-rates/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/interest-rates/settings/${LOAN_TYPE}`)
        ]);

        if (cardsRes.data.success) {
          setDynamicInterestCards(cardsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setInterestSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching interest rates data:', error);
      } finally {
        setInterestLoading(false);
      }
    };

    fetchInterestData();
  }, []);

  // Fetch dynamic FAQs and settings
  useEffect(() => {
    const fetchFAQData = async () => {
      try {
        setFaqLoading(true);
        const [faqsRes, settingsRes] = await Promise.all([
          axios.get(`/api/admin/faqs/${LOAN_TYPE}/active`),
          axios.get(`/api/admin/faqs/settings/${LOAN_TYPE}`)
        ]);

        if (faqsRes.data.success) {
          setDynamicFAQs(faqsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setFaqSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQ data:', error);
      } finally {
        setFaqLoading(false);
      }
    };

    fetchFAQData();
  }, []);

  // Fetch dynamic CTA banner settings
  useEffect(() => {
    const fetchCTABannerData = async () => {
      try {
        setCtaBannerLoading(true);
        const response = await axios.get(`/api/admin/cta-banner/settings/${LOAN_TYPE}`);

        if (response.data.success && response.data.data) {
          setCtaBannerSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA banner data:', error);
      } finally {
        setCtaBannerLoading(false);
      }
    };

    fetchCTABannerData();
  }, []);



  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const loanSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "serviceType": [
          "Home Loans",
          "Affordable Housing Finance"
        ],
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/home-construction-loan-india/#webpage",
        "url": "https://ahamhfc.com/home-construction-loan-india",
        "name": "Home Construction Loan - Aham Housing Finance",
        "description": "Apply for a home construction loan with attractive interest rates and flexible EMIs. Check eligibility, required documents, and tax benefits.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/home-construction-loan-india/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/home-construction-loan-india/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/Composite-Loan-Overview-1770783236707-121592639.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/home-construction-loan-india/#loan",
        "name": "Home Construction Loan",
        "url": "https://ahamhfc.com/home-construction-loan-india",
        "image": {
          "@id": "https://ahamhfc.com/home-construction-loan-india/#primaryimage"
        },
        "description": "Home construction loan with attractive interest rates and flexible EMIs. Check eligibility, required documents, and tax benefits.",
        "category": "Housing Finance",
        "loanType": "Home Construction Loan",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "offeredBy": {
          "@id": "https://ahamhfc.com/#financialservice"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Home buyers and self-construction borrowers"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/home-construction-loan-india/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Home Construction Loan",
            "item": "https://ahamhfc.com/home-construction-loan-india"
          }
        ]
      }
    ]
  };



  return (
    <div className="min-h-screen bg-white">

      <SEO
        title="Home Construction Loan: Attractive Rates | Aham Housing Finance"
        description="Apply for a home construction loan with attractive interest rates and flexible EMIs. Check eligibility, required documents, and tax benefits. Build with Aham!"
        keywords="Aham home construction loan, Aham Housing Finance home construction loan, home construction loan, construction loan for house, self construction home loan, loan for house construction, home building loan, housing construction finance, construction loan India, home loan for self owned land, affordable construction loan, flexible EMI construction loan, attractive interest rate home construction loan, house construction finance company, low documentation construction loan, no income proof construction loan, home construction loan eligibility, home construction loan documents, tax benefits on home construction loan, Aham Housing Finance construction loan"
        canonical="https://ahamhfc.com/home-construction-loan-india"
        ogTitle="Home Construction Loan: Attractive Rates | Aham Housing Finance"
        schema={loanSchema}
        ogDescription="Apply for a home construction loan with attractive interest rates and flexible EMIs. Check eligibility, required documents, and tax benefits. Build with Aham!"
      />


      {/* Hero Banner Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {bannerLoading ? (
          // Loading state
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('${banner?.image_url ? (banner.image_url.startsWith('http') || banner.image_url.startsWith('/') ? banner.image_url : `/${banner.image_url}`) : (banner?.backgroundImage || '/img/construction-loan/Home-Construction-Banner.webp')}')`,

              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >


                <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || "Home Construction Loan"}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || "Build your dream home with Aham's flexible financing solutions."}
                </p>

                {/* Display highlights if available */}
                {banner?.highlights && banner.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-4 mb-8">
                    {banner.highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 shadow-lg"
                      >
                        <CheckCircleOutline className="h-4 w-4 text-yellow-400" />
                        <span className="text-white text-xs md:text-sm font-medium">{highlight}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <a href={banner?.primary_button_link || '/contact-aham-housing-finance'} className="w-full sm:w-auto text-left">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0"
                    >
                      {banner?.primary_button_text || "Apply Now"}
                    </motion.button>
                  </a>

                  {banner?.secondary_button_text && (
                    <a href={banner.secondary_button_link || '#'} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0 border border-white/20"
                      >
                        {banner.secondary_button_text}
                      </motion.button>
                    </a>
                  )}
                </div>

                {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Home Construction</span>
                </motion.div>

              </motion.div>
            </div>
          </>
        )}
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>


      {/* Overview Section */}
      <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left">
                {overview?.title || 'Home Construction Loan'}
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                {overview?.description.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}

                {overview?.features && overview.features.length > 0 && (
                  <ul className="space-y-3 mt-6">
                    {overview.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircleOutline className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overview?.image_url || 'img/construction-loan/2.webp'}
                alt="Aham Housing Finance providing attractive interest rates for home construction loans on residential plots"

                className="rounded-2xl shadow-large w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>




      {/* Key Benefits Section */}
      <section id="benefits" className="relative py-24 overflow-hidden">

        {/* 🔥 Background – gradient + subtle pattern */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-1"
          style={{
            backgroundImage:
              "url('/img/construction-loan/Key-Benefits.webp')",
          }}
        ></div>

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/100 via-white/70 to-white"></div>

        <div className="relative container mx-auto px-4">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg mb-6">
              {benefitsSettings.section_title}
            </h2>
            <p className="text-lg md:text-xl text-white/90 mt-4 max-w-3xl mx-auto leading-relaxed font-medium">
              {benefitsSettings.section_subtitle}
            </p>
          </motion.div>

          {/* Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {(dynamicBenefits.length > 0 ? dynamicBenefits : benefits).map((benefit, index) => {
              const IconComponent = benefit.icon_name ? (iconMap[benefit.icon_name] || StarIcon) : benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -12, scale: 1.03 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
                >
                  {/* Floating Glow */}
                  <div className={`absolute -top-4 -right-4 w-20 h-20 bg-${benefit.gradient_from || 'primary-500'} rounded-full blur-3xl opacity-20`}></div>

                  {/* Icon */}
                  <div className={`bg-gradient-to-br from-${benefit.gradient_from || 'primary-600'} to-${benefit.gradient_to || 'primary-400'} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                    <IconComponent className="h-9 w-9 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-700 leading-relaxed font-medium">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex justify-center mt-16"
          >
            <a href={benefitsSettings.cta_link || '/contact-aham-housing-finance'} className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl w-[85%] sm:w-auto mx-auto"
              >
                {benefitsSettings.cta_text || 'Request a Quote'}
              </motion.button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Soft Background Blobs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-extrabold text-[#003366] mt-6 mb-4 leading-tight"
            >
              {eligibilitySettings.section_title}
            </motion.h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-[#ed2636] to-red-400 mx-auto rounded-full mb-6 shadow-md"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {eligibilitySettings.section_subtitle}
            </p>

            {(eligibilitySettings.calculator_notice_text || eligibilitySettings.calculator_notice_text === undefined) && (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-6"
              >
                <div
                  onClick={handleRichTextClick}
                  className="text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_a]:font-semibold [&_a]:transition-colors cursor-pointer"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeAndFormatHtml(
                      eligibilitySettings.calculator_notice_text || DEFAULT_CALCULATOR_NOTICE_TEXT
                    )
                  }}
                />
              </motion.div>
            )}
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(dynamicEligibility.length > 0 ? dynamicEligibility : eliitems).map((item, index) => {
              const IconComp = item.icon_name ? (iconMap[item.icon_name] || StarIcon) : item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, translateY: -5 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative group bg-white p-8 rounded-[32px] shadow-lg hover:shadow-2xl border border-gray-100 transition-all duration-300 flex flex-col items-center text-center"
                >
                  {/* Icon with Glowing Gradient Background */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-${item.gradient_from || item.from || 'primary-500'} to-${item.gradient_to || item.to || 'indigo-600'} flex items-center justify-center mb-6 shadow-lg group-hover:rotate-6 transition-transform duration-300`}>
                    <IconComp className="h-8 w-8 text-white" />
                  </div>

                  {/* Criteria Text */}
                  <h3 className="text-lg font-bold text-gray-900 leading-snug">
                    {item.criteria || item.text}
                  </h3>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16 pb-12">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
                px-10 py-4 rounded-full text-white font-semibold 
               bg-[#ed2636]
                shadow-lg hover:bg-primary-600 transition
              "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}
        </div>
      </section>

      {/* Documents Required Section */}
      <section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Background blur circles */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {documentsSettings.section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {documentsSettings.section_subtitle}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {((dynamicDocuments[activeTab] && dynamicDocuments[activeTab].length > 0) ? dynamicDocuments[activeTab] : documents[activeTab]).map((doc, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="
                flex items-start gap-4 
                p-5 bg-gradient-to-br from-white to-gray-50 
                rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200
              "
                  >
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                      <FileText className="h-6 w-6" />
                    </div>
                    <span className="text-gray-900 font-semibold">{doc}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

          </div>
        </div>
      </section >


      {/* Interest Rates Section */}
      < section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        < div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40" ></div >
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-8 leading-tight drop-shadow-xl">
              {interestSettings.section_title}
              <span className="block text-primary-200 mt-2">{interestSettings.section_subtitle}</span>
            </h2>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                {interestSettings.intro_text}
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-10">
                {(dynamicInterestCards.length > 0 ? dynamicInterestCards : [
                  { icon: '🏠', title: 'Flexible Rates', description: 'Rates tailored based on loan amount, tenure, credit profile & location.' },
                  { icon: '🔍', title: '100% Transparency', description: 'No hidden fees. No surprise charges. Complete clarity from start to finish.' },
                  { icon: '🤝', title: 'Dedicated Support', description: 'Our expert team guides you at every stage of your construction journey.' }
                ]).map((card, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 + index * 0.2 }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">{card.icon}</div>
                    <h4 className="text-xl font-semibold mb-2">{card.title}</h4>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {card.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                {interestSettings.closing_text}
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href={interestSettings.cta_link}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white-700 hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl"
              >
                {interestSettings.cta_text}
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section >


      {/* FAQ Section */}
      < section id="faq" className="py-20 bg-white" >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqSettings.section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {faqSettings.section_subtitle}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {(dynamicFAQs.length > 0 ? dynamicFAQs : faqs).map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                    ? 'bg-primary-50 border-2 border-primary-600'
                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {activeFAQ === index ? (
                      <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  {activeFAQ === index && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 text-gray-700 leading-relaxed"
                    >
                      {faq.answer}
                    </motion.p>
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section >

      {/* CTA Banner */}
      < section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden" >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/construction-loan/home-construction-loan.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBannerSettings.title}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaBannerSettings.description}
            </p>
            <div className="flex justify-center">
              <motion.a
                href={ctaBannerSettings.button_link || '/contact-aham-housing-finance'}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[85%] sm:w-auto mx-auto"
              >
                {ctaBannerSettings.button_text}
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomeConstructionLoan;